import { FMDQTableProps } from "@/src/common/types"
import ExportTableData from "@/src/sb/components/FMDQTable/ExportTableData"
import ColumnSelector from "@/src/sb/components/FMDQTable/Filters/ColumnSelector"
import CustomDateRange from "@/src/sb/components/FMDQTable/Filters/CustomDateRange"
import SkeletonLoader from "@/src/sb/components/FMDQTable/SkeletonLoader"
import NumberedPagination from "@/src/sb/components/Pagination/NumberedPagination"
import { applyDateRangeFilter } from "@/src/sb/core/utils/applyDateRangeFilter"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

const FMDQTable: React.FC<FMDQTableProps> = ({ columns, rows, loading }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [showCard, setShowCard] = useState<
    "dateSelect" | "columnSelect" | null
  >(null)

  const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>(
    {}
  )

  const initialCheckboxStateRef = useRef<Record<string, boolean>>({})
  const previousCheckboxStateRef = useRef<Record<string, boolean>>({})
  const [visibleColumns, setVisibleColumns] = useState(columns)
  const [filteredRows, setFilteredRows] = useState(rows)

  const rowsPerPage = 20
  const totalNumberOfRecords = filteredRows?.length

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  const handleCardDisplay = (type: "dateSelect" | "columnSelect") => {
    setShowCard((prev) => (prev === type ? null : type))
  }

  useEffect(() => {
    const initialCheckboxState = columns.reduce((acc: any, column) => {
      acc[column.key] = true
      return acc
    }, {})
    setCheckboxState(initialCheckboxState)
    setFilteredRows(rows)
    initialCheckboxStateRef.current = initialCheckboxState
  }, [columns, rows])

  const handleCheckboxChange = (id: string, checked: boolean) => {
    setCheckboxState((prevState) => ({
      ...prevState,
      [id]: checked
    }))
  }

  const currentRows = useMemo(() => {
    const page = isNaN(currentPage) || currentPage < 1 ? 1 : currentPage
    const startIndex = (page - 1) * rowsPerPage
    const endIndex = page * rowsPerPage
    return filteredRows?.slice(startIndex, endIndex) || []
  }, [filteredRows, currentPage, rowsPerPage])

  const handleApplyColumns = useCallback(() => {
    const updatedColumns = columns.filter(
      (column: any) => checkboxState[column.key]
    )
    setVisibleColumns(updatedColumns)
    const columnsToKeep = Array.from(
      new Set([...updatedColumns.map((col: any) => col.key)])
    )

    const newFilteredRows = filteredRows?.map((row: any) => {
      const filteredRow: any = {}

      Object.keys(row).forEach((key) => {
        if (columnsToKeep.includes(key)) {
          filteredRow[key] = row[key]
        } else {
          filteredRow[key] = row[key]
        }
      })

      return filteredRow
    })

    setFilteredRows(newFilteredRows)
    setShowCard(null)
  }, [columns, filteredRows, checkboxState])

  const handleCloseColumns = () => {
    previousCheckboxStateRef.current = { ...checkboxState }
    handleCardDisplay("columnSelect")
  }

  const handleApplyDateRange = useCallback(
    (option: string | null, startDate: Date | null, endDate: Date | null) => {
      const newFilteredRows = applyDateRangeFilter(
        rows,
        option,
        startDate,
        endDate
      )
      setFilteredRows(newFilteredRows)
    },
    [rows]
  )

  const handleCloseDate = () => {
    setShowCard((prev) => {
      if (prev === "dateSelect") {
        return null
      }
      return prev
    })
  }

  const handleResetDate = useCallback(() => {
    let newFilteredRows = rows
    newFilteredRows = rows
    setFilteredRows(newFilteredRows)
    setVisibleColumns(columns)
    handleCloseDate()
  }, [rows, columns])

  const handleResetColumns = () => {
    setCheckboxState(initialCheckboxStateRef.current)
  }

  return (
    <div className="relative overflow-x-auto dark:border-[#4B5563] dark:bg-[#0E0F10]">
      {loading ? (
        <SkeletonLoader />
      ) : (
        <div className="flex flex-col min-h-[60vh]">
          <section className="flex justify-between px-5 py-2.5">
            <div className="flex gap-2">
              <CustomDateRange
                showCard={showCard === "dateSelect"}
                handleCardDisplay={() => handleCardDisplay("dateSelect")}
                handleApplyDateRange={handleApplyDateRange}
                handleCloseDateRange={handleCloseDate}
                handleResetDate={handleResetDate}
              />
              <ColumnSelector
                columns={columns}
                checkboxState={checkboxState}
                handleCardDisplay={() => handleCardDisplay("columnSelect")}
                handleCheckboxChange={handleCheckboxChange}
                handleApplyColumns={handleApplyColumns}
                handleCloseColumns={handleCloseColumns}
                showCard={showCard === "columnSelect"}
                handleResetColumns={handleResetColumns}
              />
            </div>
            <ExportTableData data={filteredRows} />
          </section>
          <div className="flex-1 overflow-x-auto overflow-y-auto max-h-[500px]">
            <table className="min-w-[1200px] m-auto w-full text-sm dark:text-gray-400 font-jakarta dark:bg-[#0E0F10] dark:border-[#4B5563] text-white">
              <thead className="border-b dark:border-gray-700 dark:text-gray-400 whitespace-nowrap">
                <tr>
                  {visibleColumns?.map((column) => (
                    <th
                      key={column.key}
                      className={`px-5 py-2.5 font-normal text-xs text-[#9CA3AF] ${
                        column.key.includes("change")
                          ? "text-left"
                          : "text-center"
                      }`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-white font-mulish">
                {currentRows?.length > 0 ? (
                  currentRows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b dark:border-gray-700"
                    >
                      {visibleColumns.map((column) => {
                        const value = row[column.key]
                        const isDate =
                          value instanceof Date || !isNaN(Date.parse(value))
                        const isValidNumber =
                          !isNaN(parseFloat(value)) && isFinite(value)
                        const formattedValue = isDate
                          ? value
                          : isValidNumber
                          ? parseFloat(value).toFixed(2)
                          : ""

                        return (
                          <td
                            key={column.key}
                            className={`px-5 py-2.5 text-xs font-normal whitespace-nowrap ${
                              column.key.includes("change") &&
                              value !== "" &&
                              value !== null &&
                              value !== undefined
                                ? "text-left"
                                : "text-center"
                            }`}
                          >
                            {column.key.includes("change") ? (
                              <span
                                className={
                                  value > 0 || value === 0
                                    ? "text-[#63B3FF]"
                                    : value < 0
                                    ? "text-[#F05042]"
                                    : "text-[#63B3FF]"
                                }
                              >
                                {isValidNumber &&
                                value !== "" &&
                                value !== null &&
                                value !== undefined
                                  ? value >= 0
                                    ? `+${parseFloat(value).toFixed(2)}%`
                                    : `${parseFloat(value).toFixed(2)}%`
                                  : ""}
                              </span>
                            ) : (
                              formattedValue
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={visibleColumns.length}
                      className="min-h-[300px]"
                    >
                      <div className="flex items-center justify-center h-[300px]">
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          No data available
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && currentRows?.length > 0 ? (
        <NumberedPagination
          limit={rowsPerPage}
          totalNumberOfRecords={totalNumberOfRecords}
          currentPageNumber={currentPage}
          onPageClick={handlePageChange}
        />
      ) : null}
    </div>
  )
}

export default FMDQTable
